import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs';
import {
	AanwezigheidMeasure,
	BasicFilterExpression,
	CompoundFilterExpression,
	DataService,
	ExportFilter,
	FilterExpression,
} from '../../../services/data.service';
import { Attributes } from '../../../shared/dashboard/base-dashboard/base-dashboard-config';
import { AggregatorConfig } from '../../../shared/dashboard/base-dashboard/aggregator-config';
import { MultiAggregator, SingleAggregator, sumOver, xAgg0 } from '../../../services/aggregation';
import { createAggFunctions } from '../../../shared/dashboard/base-dashboard/base-dashboard.component';
import { unnest } from '../../../services/data-tree';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { FilterService } from '../../../services/filter.service';
import {
	formatDurationUrenMinuten,
	getWeekdagLabelKort,
	getWeekdagLabelLang,
	getWeekdagNrFromLabelKort,
	isHuidigeMaand,
} from '@cumlaude/shared-utils';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { AanwezigheidMatrixComponent, MatrixCell, MatrixData } from '../aanwezigheid-matrix/aanwezigheid-matrix.component';
import { interventielaagForPerc } from '../../../services/schoolaanwezigheid';
import { MaandDropdownState } from '../maand-dropdown-state';
import { ExportablePanel, TableExportPage } from '../../../services/exportable';
import { ExportOptions } from '../../../services/export.service';
import { ExportColumnDef } from '../../../shared/dashboard/data-tree-table/data-tree-table';
import { getFormattedValue } from '../../../shared/components/table/table/table.model';
import { formatDecimal } from '@cumlaude/shared-pipes';

export interface PerLesuurI extends Attributes {
	aw_m_nr_lesuren: number;
	aw_m_nr_lesuren_aanwezig: number;
	aw_m_nr_minuten: number;
	aw_m_nr_minuten_aanwezig: number;
	xa: any;
}

export interface PerLesuurA extends Attributes {
	aw_m_nr_lesuren: number;
	aw_m_nr_lesuren_aanwezig: number;
	aw_m_nr_minuten: number;
	aw_m_nr_minuten_x_reden: number;
	aw_m_nr_minuten_aanwezig: number;
	aw_m_nr_minuten_aanwezig_x_reden: number;
	count_records_x_reden: number;
}

@Component({
	selector: 'app-aanwezigheid-per-lesuur',
	templateUrl: './aanwezigheid-per-lesuur.component.html',
	styleUrls: ['./aanwezigheid-per-lesuur.component.scss'],
	imports: [IconComponent, SingleSelectDropdownComponent, TooltipDirective, AanwezigheidMatrixComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AanwezigheidPerLesuurComponent implements AggregatorConfig<PerLesuurI, PerLesuurA>, ExportablePanel {
	filterExpressions = input.required<FilterExpression[]>();
	permanentFilters = input.required<FilterExpression[]>();

	private readonly dataService = inject(DataService);
	private readonly filterService = inject(FilterService);

	private readonly filterExpressions$ = toObservable(this.filterExpressions);

	loading = signal(false);
	loadingChanged = output<boolean>();

	schooljaar = toSignal<string>(this.filterService.observeAsInput('aw_nm_schooljaar'));

	constructor() {
		effect(() => {
			this.loadingChanged.emit(this.loading());
		});
	}

	getSingleAggregators(): Partial<{ [ai in keyof PerLesuurA]: SingleAggregator<PerLesuurI, PerLesuurA[ai]> }> {
		return {
			aw_m_nr_lesuren: sumOver('aw_m_nr_lesuren'),
			aw_m_nr_lesuren_aanwezig: sumOver('aw_m_nr_lesuren_aanwezig'),
			aw_m_nr_minuten: sumOver('aw_m_nr_minuten'),
			aw_m_nr_minuten_x_reden: xAgg0('aw_m_nr_minuten'),
			aw_m_nr_minuten_aanwezig: sumOver('aw_m_nr_minuten_aanwezig'),
			aw_m_nr_minuten_aanwezig_x_reden: xAgg0('aw_m_nr_minuten_aanwezig'),
			count_records: sumOver('count_records'),
			count_records_x_reden: xAgg0('count_records'),
		};
	}

	getMultiAggregators(): MultiAggregator<keyof PerLesuurA, PerLesuurI, PerLesuurA, PerLesuurA[keyof PerLesuurA]>[] {
		return [];
	}

	protected perLesuurDataRoot = toSignal(
		this.filterExpressions$.pipe(
			switchMap((filters) => {
				this.loading.set(true);
				// Haal lesuur/weekdag data op voor alle maanden, met totalen over het schooljaar
				return this.dataService.getAanwezigheidData({
					g: [['aw_nr_lesuur'], ['aw_d_datum', 'per_nr_weekdag'], ['aw_nr_maand']],
					f: new CompoundFilterExpression([
						...filters,
						...this.permanentFilters(),
						new BasicFilterExpression(['aw_nr_lesuur'], null, '<>'), // alleen lessen met een lesuur
					]),
					r: [0],
					m: [AanwezigheidMeasure.LESUREN, AanwezigheidMeasure.LESTIJD],
				});
			}),
			map((resp) => {
				this.loading.set(false);
				const { aggInit, aggCombine } = createAggFunctions(this, [], resp.measures);
				const records = unnest<PerLesuurA, number[]>(resp.data, undefined, 1, aggInit, aggCombine);
				return records[0]?.[0];
			})
		)
	);

	maandDropdownState = new MaandDropdownState('lesuur-maand');

	protected lesuurMatrixData = computed<MatrixData | undefined>(() => {
		const dataRoot = this.perLesuurDataRoot();
		if (!dataRoot?.c) {
			return undefined;
		}

		const maand = this.maandDropdownState.maandMetFallback();
		const weekdagen = ['1', '2', '3', '4', '5']; // Ma t/m Vr
		const voorlopig = isHuidigeMaand(this.schooljaar(), maand.toString());

		const matrixData: MatrixData = {
			columnHeaders: weekdagen.map(getWeekdagLabelKort),
			columnClasses: weekdagen.map(() => 'onbekend'), // wordt nog gemuteerd
			rowHeaders: dataRoot.c.map((lesuurNode) => `${lesuurNode.k}e uur`),
			cells: [],
		};

		// dataRoot bevat lesuren, weekdagen, maanden

		// Voor elk lesuur (rij) - itereer direct over de children van dataRoot
		for (const lesuurNode of dataRoot.c) {
			const rijCellen: MatrixCell[] = [];

			// Voor elke weekdag (kolom)
			for (let i = 0; i < weekdagen.length; ++i) {
				const weekdag = weekdagen[i];
				const weekdagNode = lesuurNode.c?.find((wd) => wd.k === weekdag);

				// Maand gekozen => ga 1 dieper in de boom
				const sourceNode = !maand ? weekdagNode : weekdagNode?.c?.find((m) => m.k === maand.toString());

				if (!sourceNode?.a) {
					// Geen data voor deze maand/weekdag/lesuur combinatie
					rijCellen.push({ disabled: true, tooltip: 'Geen data beschikbaar' });
					continue;
				}

				// Bereken percentage (gemeenschappelijk)
				const nr_minuten_aanwezig = sourceNode.a.aw_m_nr_minuten_aanwezig ?? 0;
				const nr_minuten_gemist = sourceNode.a.aw_m_nr_minuten - nr_minuten_aanwezig;
				const nr_lesuren = sourceNode.a.aw_m_nr_lesuren ?? 0;
				const nr_lesuren_aanwezig = sourceNode.a.aw_m_nr_lesuren_aanwezig ?? 0;
				const frac = nr_minuten_aanwezig / sourceNode.a.aw_m_nr_minuten;
				const perc = frac * 100;
				const tooltipHeader = `${getWeekdagLabelLang(weekdag)} ${lesuurNode.k}e uur`;

				const tooltip = [
					{ label: '', value: tooltipHeader, bold: true },
					`${getFormattedValue(frac, 'percentage', '1.2-2')} (${interventielaagForPerc(frac)})`,
					`${formatDecimal(nr_lesuren_aanwezig)} / ${formatDecimal(nr_lesuren)} lesuren`,
					`${formatDurationUrenMinuten(nr_minuten_aanwezig, 'hu mm')} aanwezig`,
					`${formatDurationUrenMinuten(nr_minuten_gemist, 'hu mm')} gemist`,
				];

				rijCellen.push({
					value: perc,
					displayValue: `${getFormattedValue(frac, 'percentage', '1.0-0')}`,
					tooltip,
					voorlopig,
				});
				matrixData.columnClasses[i] = ''; // 1 gevuld lesuur -> hele weekdag niet meer 'onbekend'
			}

			matrixData.cells.push(rijCellen);
		}

		return matrixData;
	});

	async exportAsTable(exportOptions: ExportOptions, filters: ExportFilter[]): Promise<TableExportPage> {
		let maandLabel = this.maandDropdownState.selectedOptieLabel();
		const lesuurMatrixData = this.lesuurMatrixData()!;

		return {
			name: 'Aanwezigheid per lesuur',
			...(exportOptions.showTitle ? { title: 'Gemiddelde aanwezigheid per lesuur' } : {}),
			...(exportOptions.showFilters ? { filters, displayOptions: [{ label: 'Maand', value: maandLabel }] } : {}),
			data: [
				{
					columns: [
						{ name: '', type: 'string' },
						...lesuurMatrixData.columnHeaders.map(
							(header) =>
								<ExportColumnDef>{
									name: getWeekdagLabelLang(getWeekdagNrFromLabelKort(header)),
									type: 'percentage',
									format: '1.0-0',
								}
						),
					],
					rows: lesuurMatrixData.rowHeaders.map((rowHeader, rowIndex) => [
						rowHeader,
						...lesuurMatrixData.cells[rowIndex].map((cell) => (cell.disabled ? null : cell.value! / 100)),
					]),
				},
			],
		};
	}
}
